import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getActiveTenant } from '@peruselab/api-client/apiClient';
import { getDiscussion } from '@peruselab/api-client/discussions';
import { validateDrawingDocument } from '@peruselab/structure-editor/domain/graphDrawing';
import type { DrawingDocument } from '../generated/drawing-contract';
import { toChemMoleculeV1, moleculeSourceKey } from '@peruselab/structure-editor/domain/moleculeProjection';
import {
  buildMoleculeDrawingDocument,
  isomerSourceKey,
  reconstructDrawingDocument,
  toIsomerCreatePayload
} from '@peruselab/structure-editor/domain/moleculeDrawingProjection';
import { createMolecule, getMolecule, publishMolecule } from '@peruselab/api-client/molecules';
import { createIsomer, getIsomer } from '@peruselab/api-client/isomers';
import {
  archiveMoleculeDrawing,
  createMoleculeDrawing,
  getMoleculeDrawing,
  listPrivateMolecules,
  MoleculeDrawingApiError,
  restoreMoleculeDrawing,
  updateMoleculeDrawing,
  setMoleculeDrawingVisibility,
  type DrawingVisibility
} from '@peruselab/api-client/moleculeDrawings';
import {
  createReaction,
  getReactionWithParticipants,
  listReactionPrivateMolecules,
  setReactionVisibility,
  updateReaction,
  type ExpandedReactionParticipant,
  type ReactionResponse
} from '@peruselab/api-client/reactions';
import { announceMoleculeVisibility } from '../features/chemistry/molecule/moleculeVisibilitySync';
import type { ReactionDrawingDocument } from '@peruselab/structure-editor/chemistry/reactionParticipant';
import {
  drawnHydrogenIds,
  remapStructureIds,
  type StructureCorrespondence
} from '@peruselab/structure-editor/domain/structureIdRemap';
import { asSaved, placedOnPage } from '@peruselab/structure-editor/rendering/contentExtent';
import { regionalDefaultPage } from '@peruselab/structure-editor/domain/page';

// #147: speaks v11 natively -- POST/PUT /api/molecules + /api/isomers + /api/molecule-drawings
// (#146) directly, replacing the v10-shimmed /api/drawings this hook used before. graphDrawing.ts's
// internal DrawingDocument shape is unchanged (#144 decision 3); only this save/load boundary is
// new, via domain/moleculeDrawingProjection.ts.

/** #1060 (D13, O1): the answer to "make this drawing public?" when it shows private molecules. */
export type PublishDrawingChoice = 'cancel' | 'drawing' | 'drawingAndMolecules';

interface RemoteDrawingState {
  readonly id: string;
  readonly documentRevision: number;
  readonly savedDocument: string;
  readonly savedTitle: string;
  readonly archivedAt: string | null;
  readonly moleculeId: string | null;
  readonly moleculeRevision: number;
  readonly isomerId: string | null;
  readonly isomerRevision: number;
  // #811 -- private (the default) or public.
  readonly visibility: DrawingVisibility;
  /** #615 -- the editor's atom/bond IDs -> the reused Molecule's, when saving matched an existing
   *  Molecule numbered differently; null when the editor's IDs are the Molecule's own. */
  readonly correspondence: StructureCorrespondence | null;
  /** #1051 (P6) -- the tenant it was opened or saved in (null: personal). Its saves go back there,
   *  whatever tenant is active now. */
  readonly tenantId: string | null;
}

type PersistenceState =
  | { readonly state: 'idle' }
  | { readonly state: 'loading' }
  | { readonly state: 'saving' }
  | { readonly state: 'success'; readonly message: string }
  | { readonly state: 'error'; readonly message: string };

/**
 * #1169: what a reaction carries beyond its drawing -- its conditions and catalysts
 * (useReactionDetails) -- as one comparable key, so changing them is an unsaved change.
 */
export function reactionDetailsKey(conditionFields: unknown, catalystAtomIds: Iterable<string>): string {
  return JSON.stringify([conditionFields, [...catalystAtomIds].sort()]);
}

/** #1169: the paper a reaction opens onto, as its `apply` lays it out. */
export interface LaidReactionPaper {
  readonly document: DrawingDocument;
  /** reactionDetailsKey of the conditions and catalysts it was opened with. */
  readonly details: string;
}

export function useDrawingPersistence(
  document: DrawingDocument,
  replaceDocument: (next: DrawingDocument) => void,
  /** #1169: reactionDetailsKey of the paper's conditions and catalysts. */
  reactionDetails = ''
) {
  const { t } = useTranslation();
  // #882: messages name the drawing by its title and say "version" -- never the raw ID.
  const drawingName = useCallback(
    (title: string | null | undefined) => title?.trim() || t('persistencePanel.untitledDrawing'),
    [t]
  );
  const conflictMessage = useCallback(
    (currentRevision: number | undefined) =>
      currentRevision === undefined
        ? t('persistencePanel.messages.conflictUnknownVersion')
        : t('persistencePanel.messages.conflict', { version: currentRevision }),
    [t]
  );
  const [drawingTitle, setDrawingTitle] = useState('');
  const [remoteDrawing, setRemoteDrawing] = useState<RemoteDrawingState | null>(null);
  const [persistence, setPersistence] = useState<PersistenceState>({ state: 'idle' });
  const [remoteReaction, setRemoteReaction] = useState<{
    readonly id: string;
    readonly documentRevision: number;
    readonly tenantId: string | null;
    /** #1124: a reaction is public with its drawing, switched as one (#811). */
    readonly visibility: DrawingVisibility;
    /** #1169: the paper as opened or last saved (reactionPaperKey), to tell unsaved changes by. */
    readonly savedPaper: string;
  } | null>(null);

  const openDrawingId = remoteDrawing?.id;
  // #1051 (P6): the open drawing keeps its own tenant; any other (a My drawings card) is the active one.
  const openDrawingTenant = remoteDrawing?.tenantId;
  const tenantOf = useCallback(
    (id: string) => (id === openDrawingId ? openDrawingTenant : undefined),
    [openDrawingId, openDrawingTenant]
  );
  // #1143: compared as they would be saved -- cut down to the artwork. Opened, a drawing grows to
  // the paper's shape (useDocumentHistory) without being edited; that growth is never saved, so it
  // is no unsaved change.
  const serializedDocument = useMemo(() => JSON.stringify(asSaved(document)), [document]);
  const serializedSaved = useMemo(
    () => (remoteDrawing ? JSON.stringify(asSaved(JSON.parse(remoteDrawing.savedDocument) as DrawingDocument)) : null),
    [remoteDrawing]
  );
  // #1169: a reaction is compared as a drawing is -- cut down to the artwork -- with its
  // conditions, catalysts and title. Its saved document is built from the paper, so the paper as
  // it was opened or saved stands for it.
  const drawnDocument = useMemo(() => asDrawn(document), [document]);
  const reactionPaper = reactionPaperKey(drawnDocument, reactionDetails, drawingTitle);
  const hasUnsavedChanges = remoteDrawing
    ? serializedDocument !== serializedSaved || drawingTitle.trim() !== remoteDrawing.savedTitle
    : remoteReaction
      ? reactionPaper !== remoteReaction.savedPaper
      : true;
  // ADR 0016 part C: whether the atoms and bonds on the paper are still the saved Molecule's --
  // moving atoms or renaming the drawing keeps them; adding or removing atoms or bonds doesn't.
  const savedMoleculeKey = useMemo(
    () => (remoteDrawing ? moleculeSourceKey(JSON.parse(remoteDrawing.savedDocument) as DrawingDocument) : null),
    [remoteDrawing]
  );
  const structureMatchesSaved = useMemo(
    () => savedMoleculeKey !== null && moleculeSourceKey(document) === savedMoleculeKey,
    [document, savedMoleculeKey]
  );

  const loadDrawingById = useCallback(
    async (drawingId: string) => {
      const id = drawingId.trim();
      if (!id) {
        setPersistence({ state: 'error', message: t('persistencePanel.messages.enterId') });
        return;
      }
      setPersistence({ state: 'loading' });
      try {
        const moleculeDrawing = await getMoleculeDrawing(id);
        const [ref] = moleculeDrawing.document.isomerRefs;
        const isomer = ref ? await getIsomer(ref.isomerId) : null;
        const molecule = isomer ? await getMolecule(isomer.moleculeId) : null;
        // #1143 (ADR 0025 D7): saved as its artwork's area, it's set in from the page's corner; saved
        // without a page (none was chosen), it opens on the reader's own -- not the last drawing's.
        const reconstructed = placedOnPage(
          reconstructDrawingDocument(moleculeDrawing.document, molecule?.document, isomer?.document)
        );
        const loadedDocument = validateDrawingDocument(
          reconstructed.page ? reconstructed : { ...reconstructed, page: regionalDefaultPage() }
        );
        replaceDocument(loadedDocument);
        setRemoteReaction(null);
        setRemoteDrawing({
          id: moleculeDrawing.id,
          documentRevision: moleculeDrawing.documentRevision,
          savedDocument: JSON.stringify(loadedDocument),
          savedTitle: moleculeDrawing.title ?? '',
          archivedAt: moleculeDrawing.archivedAt,
          moleculeId: molecule?.id ?? null,
          moleculeRevision: molecule?.documentRevision ?? 0,
          isomerId: isomer?.id ?? null,
          isomerRevision: isomer?.documentRevision ?? 0,
          visibility: moleculeDrawing.visibility ?? 'private',
          // A loaded drawing is built from the Molecule itself: its IDs are the Molecule's.
          correspondence: null,
          // Opened from the active tenant's drawings.
          tenantId: getActiveTenant()
        });
        setDrawingTitle(moleculeDrawing.title ?? '');
        // #882: the status line already says which drawing and version is open.
        setPersistence({ state: 'idle' });
      } catch (error) {
        setPersistence({
          state: 'error',
          message: error instanceof Error ? error.message : t('persistencePanel.messages.openFailed')
        });
      }
    },
    [replaceDocument, t]
  );

  const saveDrawing = useCallback(
    async (title = drawingTitle) => {
      const documentToSave = document;
      setPersistence({ state: 'saving' });
      try {
        let moleculeId: string | null = null;
        let moleculeRevision = 0;
        let isomerId: string | null = null;
        let isomerRevision = 0;
        // #615: when saving reuses an identical Molecule numbered differently, everything saved with
        // it -- the Isomer's stereo, the drawing's coordinates, labels, styles and annotations -- is
        // renamed onto the Molecule's own atom and bond IDs. Kept for the open drawing, so a later
        // save with the same structure is renamed the same way.
        let correspondence = remoteDrawing?.correspondence ?? null;
        let needsIsomer = false;
        const hasChemistry = documentToSave.structure.graph.nodes.length > 0;

        const previousDocument = remoteDrawing ? (JSON.parse(remoteDrawing.savedDocument) as DrawingDocument) : null;
        const constitutionChanged =
          !previousDocument || moleculeSourceKey(documentToSave) !== moleculeSourceKey(previousDocument);
        if (!hasChemistry) {
          correspondence = null;
        } else if (constitutionChanged || !remoteDrawing?.moleculeId) {
          const molecule = await createMolecule(toChemMoleculeV1(documentToSave));
          moleculeId = molecule.id;
          moleculeRevision = molecule.documentRevision;
          correspondence = molecule.correspondence ?? null;
          needsIsomer = true;
        } else {
          moleculeId = remoteDrawing!.moleculeId;
          moleculeRevision = remoteDrawing!.moleculeRevision;
          needsIsomer = isomerSourceKey(documentToSave) !== isomerSourceKey(previousDocument!);
          if (!needsIsomer) {
            isomerId = remoteDrawing!.isomerId;
            isomerRevision = remoteDrawing!.isomerRevision;
          }
        }
        const aligned = correspondence ? remapStructureIds(documentToSave, correspondence) : documentToSave;
        // #951: hydrogens drawn as atoms that the reused Molecule holds as implicit stay on the drawing.
        const drawnHydrogens = correspondence ? drawnHydrogenIds(aligned, correspondence) : {};
        if (hasChemistry && moleculeId && (needsIsomer || isomerId === null)) {
          const isomer = await createIsomer(
            toIsomerCreatePayload(aligned, moleculeId, moleculeRevision, drawnHydrogens)
          );
          isomerId = isomer.id;
          isomerRevision = isomer.documentRevision;
        }

        // #1143 (ADR 0025 D7): saved as its artwork's area, with a page only if its author chose one.
        const moleculeDrawingDocument = buildMoleculeDrawingDocument(
          asSaved(aligned),
          isomerId,
          isomerRevision,
          drawnHydrogens
        );
        const stored = remoteDrawing
          ? await updateMoleculeDrawing(
              remoteDrawing.id,
              remoteDrawing.documentRevision,
              moleculeDrawingDocument,
              title,
              remoteDrawing.tenantId
            )
          : await createMoleculeDrawing(moleculeDrawingDocument, title);

        setRemoteDrawing({
          id: stored.id,
          documentRevision: stored.documentRevision,
          savedDocument: JSON.stringify(documentToSave),
          savedTitle: stored.title ?? '',
          archivedAt: stored.archivedAt,
          moleculeId,
          moleculeRevision,
          isomerId,
          isomerRevision,
          visibility: stored.visibility ?? 'private',
          correspondence,
          // A new drawing belongs to the active tenant; a saved one stays where it was.
          tenantId: remoteDrawing ? remoteDrawing.tenantId : getActiveTenant()
        });
        setDrawingTitle(stored.title ?? '');
        // #882: the status line ("… · version N · saved") is the confirmation.
        setPersistence({ state: 'idle' });
        return true;
      } catch (error) {
        const conflict = error instanceof MoleculeDrawingApiError && error.status === 409;
        setPersistence({
          state: 'error',
          message: conflict
            ? conflictMessage(error.currentRevision)
            : error instanceof Error
              ? error.message
              : t('persistencePanel.messages.saveFailed')
        });
        return false;
      }
    },
    [document, drawingTitle, remoteDrawing, t, conflictMessage]
  );

  // #705: a drawing with a reaction arrow saves as a reaction (ADR 0014 §5). The reaction saved or
  // opened here is remembered, so saving again updates it rather than creating another.
  const saveReaction = useCallback(
    async (build: () => Promise<ReactionDrawingDocument>, title = drawingTitle) => {
      setPersistence({ state: 'saving' });
      try {
        const reactionDocument = await build();
        const stored = remoteReaction
          ? await updateReaction(
              remoteReaction.id,
              remoteReaction.documentRevision,
              reactionDocument,
              title,
              remoteReaction.tenantId
            )
          : await createReaction(reactionDocument, title);
        setRemoteReaction({
          id: stored.id,
          documentRevision: stored.documentRevision,
          tenantId: remoteReaction ? remoteReaction.tenantId : getActiveTenant(),
          visibility: stored.visibility ?? 'private',
          // The paper as it was when Save was pressed: an edit made while saving stays unsaved.
          savedPaper: reactionPaperKey(drawnDocument, reactionDetails, stored.title ?? '')
        });
        setRemoteDrawing(null);
        setDrawingTitle(stored.title ?? '');
        setPersistence({
          state: 'success',
          message: remoteReaction
            ? t('persistencePanel.messages.reactionSaved', { version: stored.documentRevision })
            : t('persistencePanel.messages.reactionCreated', {
                title: stored.title?.trim() || t('persistencePanel.untitledReaction')
              })
        });
        return true;
      } catch (error) {
        setPersistence({
          state: 'error',
          message: error instanceof Error ? error.message : t('persistencePanel.messages.reactionSaveFailed')
        });
        return false;
      }
    },
    [drawingTitle, drawnDocument, reactionDetails, remoteReaction, t]
  );

  /** #705: opens a saved reaction onto the paper; `apply` lays it out (the caller owns the paper). */
  const openReaction = useCallback(
    async (
      id: string,
      apply: (
        reaction: ReactionResponse & { readonly participants: readonly ExpandedReactionParticipant[] }
      ) => Promise<LaidReactionPaper>,
      // #1136: someone else's public reaction is not opened for editing -- it goes here instead.
      notYours?: (reaction: ReactionResponse) => void
    ) => {
      setPersistence({ state: 'loading' });
      try {
        const reaction = await getReactionWithParticipants(id);
        if (reaction.owned === false && notYours) {
          setPersistence({ state: 'idle' });
          notYours(reaction);
          return;
        }
        const laid = await apply(reaction);
        setRemoteReaction({
          id: reaction.id,
          documentRevision: reaction.documentRevision,
          tenantId: getActiveTenant(),
          visibility: reaction.visibility ?? 'private',
          savedPaper: reactionPaperKey(asDrawn(laid.document), laid.details, reaction.title ?? '')
        });
        setRemoteDrawing(null);
        setDrawingTitle(reaction.title ?? '');
        setPersistence({
          state: 'success',
          message: t('persistencePanel.messages.reactionOpened', {
            title: reaction.title?.trim() || t('persistencePanel.untitledReaction'),
            version: reaction.documentRevision
          })
        });
      } catch (error) {
        setPersistence({
          state: 'error',
          message: error instanceof Error ? error.message : t('persistencePanel.messages.reactionOpenFailed')
        });
      }
    },
    [t]
  );

  // #1060 (D13): publishing a drawing that shows the publisher's private molecules asks first --
  // the picture discloses the structures; the molecules stay private unless published too (O1).
  const [publishPrompt, setPublishPrompt] = useState<{ readonly privateMolecules: number } | null>(null);
  const publishAnswer = useRef<((choice: PublishDrawingChoice) => void) | null>(null);
  const answerPublishPrompt = useCallback((choice: PublishDrawingChoice) => {
    publishAnswer.current?.(choice);
    publishAnswer.current = null;
    setPublishPrompt(null);
  }, []);
  const askToPublish = useCallback(
    (privateMolecules: number) =>
      new Promise<PublishDrawingChoice>((resolve) => {
        publishAnswer.current = resolve;
        setPublishPrompt({ privateMolecules });
      }),
    []
  );

  /** #811 -- make the open (saved) drawing public or private again; #1124: or the open reaction. */
  const changeVisibility = useCallback(
    async (id: string, visibility: DrawingVisibility) => {
      const reaction = remoteReaction?.id === id ? remoteReaction : null;
      const tenantId = reaction ? reaction.tenantId : tenantOf(id);
      let moleculesToPublish: readonly string[] = [];
      if (visibility === 'public') {
        const privateMolecules = await (
          reaction ? listReactionPrivateMolecules(id, tenantId) : listPrivateMolecules(id, tenantId)
        ).catch(() => [] as readonly string[]);
        if (privateMolecules.length > 0) {
          const choice = await askToPublish(privateMolecules.length);
          if (choice === 'cancel') return;
          if (choice === 'drawingAndMolecules') moleculesToPublish = privateMolecules;
        }
      }
      // ADR 0016 D14 (#886 Q5): narrowing the audience hides other people's comments from them --
      // say so first, and only then (no other comments: still one click, #820).
      // (A reaction has no discussion of its own.)
      if (visibility === 'private' && !reaction) {
        const byOthers = await getDiscussion('molecule-drawing', id)
          .then((thread) => thread.byOthers)
          .catch(() => null);
        if (
          byOthers &&
          byOthers.comments > 0 &&
          !globalThis.confirm(
            t('discussions.narrowingWarning', {
              comments: t('discussions.narrowingComments', { count: byOthers.comments }),
              people: t('discussions.narrowingPeople', { count: byOthers.people })
            })
          )
        ) {
          return;
        }
      }
      setPersistence({ state: 'saving' });
      try {
        const stored = reaction
          ? await setReactionVisibility(id, visibility, tenantId)
          : await setMoleculeDrawingVisibility(id, visibility, tenantId);
        const now = stored.visibility ?? visibility;
        if (reaction) setRemoteReaction((current) => (current?.id === id ? { ...current, visibility: now } : current));
        else setRemoteDrawing((current) => (current?.id === id ? { ...current, visibility: now } : current));
        try {
          for (const moleculeId of moleculesToPublish) {
            await publishMolecule(moleculeId, tenantId);
            announceMoleculeVisibility(moleculeId, 'public');
          }
        } catch {
          setPersistence({ state: 'error', message: t('persistencePanel.messages.moleculesNotPublished') });
          return;
        }
        setPersistence({
          state: 'success',
          message:
            now !== 'public'
              ? t('persistencePanel.messages.nowPrivate')
              : moleculesToPublish.length > 0
                ? t('persistencePanel.messages.nowPublicWithMolecules', { count: moleculesToPublish.length })
                : t('persistencePanel.messages.nowPublic')
        });
      } catch (error) {
        setPersistence({
          state: 'error',
          message: error instanceof Error ? error.message : t('persistencePanel.messages.visibilityFailed')
        });
      }
    },
    [t, tenantOf, askToPublish, remoteReaction]
  );

  // Resolves true once the server has archived / restored it (#837: the My drawings feed then
  // refreshes).
  const changeArchiveStatus = useCallback(
    async (id: string, documentRevision: number, archived: boolean) => {
      if (archived && !globalThis.confirm(t('persistencePanel.messages.archiveConfirm'))) return false;
      setPersistence({ state: 'saving' });
      try {
        const stored = archived
          ? await archiveMoleculeDrawing(id, documentRevision, tenantOf(id))
          : await restoreMoleculeDrawing(id, documentRevision, tenantOf(id));
        setRemoteDrawing((current) =>
          current?.id === id
            ? {
                ...current,
                documentRevision: stored.documentRevision,
                archivedAt: stored.archivedAt
              }
            : current
        );
        // #882: for the open drawing the status line shows "· archived" appear or go; only a
        // drawing restored from the My drawings feed needs a message.
        setPersistence(
          id === openDrawingId
            ? { state: 'idle' }
            : {
                state: 'success',
                message: t(archived ? 'persistencePanel.messages.archived' : 'persistencePanel.messages.restored', {
                  title: drawingName(stored.title)
                })
              }
        );
        return true;
      } catch (error) {
        const conflict = error instanceof MoleculeDrawingApiError && error.status === 409;
        setPersistence({
          state: 'error',
          message: conflict
            ? conflictMessage(error.currentRevision)
            : error instanceof Error
              ? error.message
              : t(archived ? 'persistencePanel.messages.archiveFailed' : 'persistencePanel.messages.restoreFailed')
        });
        return false;
      }
    },
    [t, drawingName, conflictMessage, openDrawingId, tenantOf]
  );

  const resetForNewDrawing = useCallback(() => {
    setRemoteDrawing(null);
    setRemoteReaction(null);
    setDrawingTitle('');
    setPersistence({ state: 'idle' });
  }, []);

  const updateDrawingTitle = useCallback((value: string) => {
    setDrawingTitle(value);
    setPersistence({ state: 'idle' });
  }, []);

  return {
    drawingTitle,
    updateDrawingTitle,
    remoteDrawing,
    persistence,
    hasUnsavedChanges,
    structureMatchesSaved,
    loadDrawingById,
    saveDrawing,
    remoteReaction,
    /** #1051: the tenant the open drawing or reaction belongs to (null: personal); undefined if none. */
    openTenantId: remoteDrawing ? remoteDrawing.tenantId : remoteReaction ? remoteReaction.tenantId : undefined,
    saveReaction,
    openReaction,
    changeArchiveStatus,
    changeVisibility,
    publishPrompt,
    answerPublishPrompt,
    resetForNewDrawing
  };
}

/**
 * #1169 (owner test): a reaction's paper as drawn -- cut down to the artwork, without what the
 * chemistry check writes onto it once opened, outside the undo history: perceived aromaticity and
 * stereo (derivedPropertySets, the structure's stereochemistry) and the revisions that count them.
 * A reaction with a benzene ring otherwise read as unsaved as soon as it was opened.
 */
function asDrawn(document: DrawingDocument): string {
  const saved = asSaved(document);
  const { revision: _revision, stereochemistry: _stereochemistry, graph, ...structure } = saved.structure;
  const { revision: _graphRevision, ...graphAsDrawn } = graph;
  const {
    revision: _depictionRevision,
    structureRevision: _structureRevision,
    coordinateSet,
    ...depiction
  } = saved.depiction;
  const { structureRevision: _coordinatesRevision, ...coordinates } = coordinateSet;
  return JSON.stringify({
    ...saved,
    derivedPropertySets: [],
    structure: { ...structure, graph: graphAsDrawn },
    depiction: { ...depiction, coordinateSet: coordinates }
  });
}

/** #1169: a reaction's paper as one key -- its document as drawn, its details and its title. */
function reactionPaperKey(serializedDocument: string, details: string, title: string): string {
  return `${serializedDocument}|${details}|${title.trim()}`;
}
